import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Skeleton,
  Switch,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { generatePath } from "react-router";
import CDS from "design-system";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { companyKeys, useCurrentCompanyQuery } from "features/Company/hooks";
import { Company } from "models/company";
import apiClient from "services/ApiClient";

export default function PersonalEmailPortalAccessSettings() {
  return (
    <Card minWidth="900px" width="calc(100% - 200px)" mt="4">
      <CardBody>
        <Flex justifyContent="space-between" w="100%" alignItems="center" p={5}>
          <CDS.Heading22 fontWeight="medium" color="fg.muted">
            New Hire Preboarding Portal
          </CDS.Heading22>
        </Flex>
        <VStack align="stretch" spacing={4} px={10} pb={6}>
          <CDS.Text14 color="content.secondary">
            Allow new hires to sign into their onboarding portal with their personal email before they start.
            After their start date, personal-email sign-in is automatically disabled and they must use their
            work account.
          </CDS.Text14>
          <PersonalEmailToggle />
        </VStack>
      </CardBody>
    </Card>
  );
}

function PersonalEmailToggle() {
  const { data: company, isLoading } = useCurrentCompanyQuery();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [confirmingOff, setConfirmingOff] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.put<Company>(generatePath(API_ROUTES.companies.show, { id: "current" }), {
        company: { personal_email_portal_access_enabled: enabled },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companyKeys.current() });
      setConfirmingOff(false);
      toast({ title: "Saved", status: "success" });
    },
    onError: () => {
      setConfirmingOff(false);
      toast({ title: "Could not save", status: "error" });
    },
  });

  if (isLoading || !company) {
    return <Skeleton h="50px" />;
  }

  const enabled = company.personalEmailPortalAccessEnabled;

  function handleChange(next: boolean) {
    if (next === enabled) return;
    if (!next) {
      setConfirmingOff(true);
      return;
    }
    mutate(true);
  }

  return (
    <>
      <HStack align="center" spacing={4}>
        <Box flex={1}>
          <CDS.Text14 fontWeight="medium">Allow personal-account sign-in</CDS.Text14>
          <CDS.Text12 color="content.secondary">
            When off, new hires must wait for their work account to access their portal.
          </CDS.Text12>
        </Box>
        <Switch
          isChecked={enabled}
          onChange={(e) => handleChange(e.target.checked)}
          isDisabled={isPending}
          colorScheme="cdsIndigo"
        />
      </HStack>

      <AlertDialog
        isOpen={confirmingOff}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmingOff(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="semibold">
              Turn off personal-account sign-in?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              New hires won&apos;t be able to access their portal with their personal email until they have a
              work account active. Anyone currently signed in with a personal account will see a message
              asking them to contact their hiring lead.
            </AlertDialogBody>
            <AlertDialogFooter>
              <HStack spacing={3}>
                <CDS.Button
                  ref={cancelRef}
                  intent={CDS.Button.intent.secondary}
                  variant={CDS.Button.variant.ghost}
                  onClick={() => setConfirmingOff(false)}
                  disabled={isPending}
                >
                  Cancel
                </CDS.Button>
                <CDS.Button
                  intent={CDS.Button.intent.indigo}
                  onClick={() => mutate(false)}
                  loading={isPending}
                >
                  Turn off
                </CDS.Button>
              </HStack>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
